import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deletePromo, listEventPromos, savePromo, setPromoActive, type SavePromoInput } from '@/lib/api/promos';

const KEYS = {
  all: ['event-promos'] as const,
  byEvent: (eventId: string) => [...KEYS.all, eventId] as const,
};

export const useEventPromos = (eventId: string | null | undefined) =>
  useQuery({
    queryKey: KEYS.byEvent(eventId ?? ''),
    queryFn: () => listEventPromos(eventId!),
    enabled: !!eventId,
    staleTime: 60 * 1000,
  });

function usePromoMutation<TArgs>(fn: (a: TArgs) => Promise<unknown>, eventIdOf: (a: TArgs) => string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (_, a) => qc.invalidateQueries({ queryKey: KEYS.byEvent(eventIdOf(a)) }),
  });
}

export const useSavePromo = () => usePromoMutation((a: SavePromoInput) => savePromo(a), (a) => a.eventId);
export const useSetPromoActive = () =>
  usePromoMutation((a: { id: string; eventId: string; isActive: boolean }) => setPromoActive(a.id, a.isActive), (a) => a.eventId);
export const useDeletePromo = () =>
  usePromoMutation((a: { id: string; eventId: string }) => deletePromo(a.id), (a) => a.eventId);
